// src/domain/nudges.ts
// Heimdall capture-time guidance (AI Analyst brief §3): a pure function that turns a
// capture's mock properties into dismissible nudges. None of these block submission;
// they help the reporter fix problems before an approver ever sees them.

export type Nudge = { id: string; severity: "info" | "attention"; message: string };

/** Great-circle distance in metres between two lat/lng points (haversine). */
export function distanceMeters(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6_371_000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(x)));
}

export type CaptureInput = {
  geo?: { lat: number; lng: number };
  siteGeo?: { lat: number; lng: number };
  siteName?: string;
  /** On-site tolerance in metres before the geo nudge fires. */
  siteRadiusM?: number;
  /** 0..1; above 0.7 reads as blurred. */
  blurScore?: number;
  /** ISO date the document was extracted as. */
  documentDate?: string;
  /** ISO date the work was claimed for. */
  claimedDate?: string;
};

/** Deterministically evaluate a capture and return any nudges, most serious first. */
export function evaluateCapture(input: CaptureInput): Nudge[] {
  const nudges: Nudge[] = [];

  if (input.blurScore != null && input.blurScore > 0.7) {
    nudges.push({ id: "blur", severity: "attention", message: "This photo looks blurred. Try again in better light." });
  }

  if (input.geo && input.siteGeo) {
    const d = distanceMeters(input.geo, input.siteGeo);
    const radius = input.siteRadiusM ?? 500;
    if (d > radius) {
      const km = Math.round(d / 100) / 10;
      const dist = km >= 1 ? `${km} km` : `${Math.round(d)} m`;
      nudges.push({
        id: "geo",
        severity: "attention",
        message: `This location reads about ${dist} from ${input.siteName ?? "the project site"}. Move to the site or add a note.`,
      });
    }
  }

  if (input.documentDate && input.claimedDate) {
    const diffDays = Math.abs(new Date(input.documentDate).getTime() - new Date(input.claimedDate).getTime()) / 86_400_000;
    if (diffDays > 14) {
      nudges.push({
        id: "date",
        severity: "info",
        message: `This document is dated ${input.documentDate}; the work was claimed for ${input.claimedDate}. Confirm the right document is attached.`,
      });
    }
  }

  return nudges;
}
